import { logError } from "modules/helpers/logger/loggerSlice";
import { cubeLoadExtended } from "modules/helpers/cube/cubeSlice";
import dateUtils from "utils/dateUtils";

import { selectStore, selectComparator, selectReferenceDate, selectCostReferenceDate } from "modules/customer/insights/portfolio/portfolioSlice";

import actions from "./actions";

const getSalesPerSquareFoot = () => async (dispatch, getState) => {
    dispatch(actions.getSalesPerSquareFootRequest());
    try {
        const state = getState();
        const currentDate = selectReferenceDate(state);
        const selectedStoreSelector = selectStore(state);
        const selectedStoreID = selectedStoreSelector.id;
        const comparatorStoresSelector = selectComparator(state);
        const comparatorStoresIDs = comparatorStoresSelector.getStores().map(store => store.id);
        const selectedAndComparatorIDs = [selectedStoreID, ...comparatorStoresIDs];
        const priorTwelveMonthsStartDate = dateUtils.priorTwelveMonthsStartDate(currentDate);

        const query = {
            measures: ["F_Sales.SumLineValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorTwelveMonthsStartDate, currentDate]
            }],
            dimensions: ["D_Store.StoreNaturalID", "D_Store.Sqft"],
            filters: [{
                member: "D_Store.StoreNaturalID",
                operator: "equals",
                values: selectedAndComparatorIDs
            }]
        };

        const resultSet = await dispatch(cubeLoadExtended(query));

        const sales = resultSet.loadResponses[0].data.map(item => ({
            store: item["D_Store.StoreNaturalID"],
            sales: item["F_Sales.SumLineValue"],
            salesPerSqft: (item["D_Store.Sqft"] === 0) ? 0 : item["F_Sales.SumLineValue"] / item["D_Store.Sqft"]
        }));

        const selectedStore = sales.find(item => item.store === selectedStoreID) || {};
        const comparatorStores = sales.filter(item => comparatorStoresIDs.includes(item.store));

        dispatch(actions.getSalesPerSquareFootSuccess(sales, selectedStore, comparatorStores));
    }
    catch (error) {
        dispatch(actions.getSalesPerSquareFootFailure());
        dispatch(logError("Error loading SalesPerSquareFoot.", error));
    }
};

const getPropertyCostsPerSquareFoot = () => async (dispatch, getState) => {
    dispatch(actions.getPropertyCostsPerSquareFootRequest());
    try {
        const state = getState();
        const currentDate = selectReferenceDate(state);
        const selectedStoreSelector = selectStore(state);
        const selectedStoreID = selectedStoreSelector.id;
        const comparatorStoresSelector = selectComparator(state);
        const comparatorStoresIDs = comparatorStoresSelector.getStores().map(store => store.id);
        const selectedAndComparatorIDs = [selectedStoreID, ...comparatorStoresIDs];
        const priorTwelveMonthsStartDate = dateUtils.priorTwelveMonthsStartDate(currentDate);

        const query = {
            measures: ["F_Cost.SumPropertyCost"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorTwelveMonthsStartDate, currentDate]
            }],
            dimensions: ["D_Store.StoreNaturalID", "D_Store.Sqft"],
            filters: [{
                member: "D_Store.StoreNaturalID",
                operator: "equals",
                values: selectedAndComparatorIDs
            }]
        };

        const resultSet = await dispatch(cubeLoadExtended(query));

        const propertyCost = resultSet.loadResponses[0].data.map(item => ({
            store: item["D_Store.StoreNaturalID"],
            propertyCost: item["F_Cost.SumPropertyCost"],
            propertyCostPerSqft: (item["D_Store.Sqft"] === 0) ? 0 : item["F_Cost.SumPropertyCost"] / item["D_Store.Sqft"]
        }));

        const selectedStore = propertyCost.find(item => item.store === selectedStoreID) || {};
        const comparatorStores = propertyCost.filter(item => comparatorStoresIDs.includes(item.store));

        dispatch(actions.getPropertyCostsPerSquareFootSuccess(propertyCost, selectedStore, comparatorStores));
    }
    catch (error) {
        dispatch(actions.getPropertyCostsPerSquareFootFailure());
        dispatch(logError("Error loading PropertyCostsPerSquareFoot.", error));
    }
};

const getSalesPerPoundOfPropertyCost = () => async (dispatch, getState) => {
    dispatch(actions.getSalesPerPoundOfPropertyCostRequest());
    try {
        const state = getState();
        const referenceDate = selectCostReferenceDate(state);
        const selectedStoreSelector = selectStore(state);
        const selectedStoreID = selectedStoreSelector.id;
        const comparatorStoresSelector = selectComparator(state);
        const comparatorStoresIDs = comparatorStoresSelector.getStores().map(store => store.id);
        const selectedAndComparatorIDs = [selectedStoreID, ...comparatorStoresIDs];
        const priorTwelveMonthsToReferenceDate = referenceDate.minus({ months: 12 }).plus({ days: 1 });

        const query = {
            measures: ["F_Sales.SumLineValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorTwelveMonthsToReferenceDate, referenceDate]
            }],
            dimensions: ["D_Store.StoreNaturalID"],
            filters: [{
                member: "D_Store.StoreNaturalID",
                operator: "equals",
                values: selectedAndComparatorIDs
            }]
        };

        const resultSet = await dispatch(cubeLoadExtended(query));

        const sales = resultSet.loadResponses[0].data.map(item => ({
            store: item["D_Store.StoreNaturalID"],
            sales: item["F_Sales.SumLineValue"]
        }));

        const propertyCostQuery = {
            measures: ["F_Cost.SumPropertyCost"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorTwelveMonthsToReferenceDate, referenceDate]
            }],
            dimensions: ["D_Store.StoreNaturalID"],
            filters: [{
                member: "D_Store.StoreNaturalID",
                operator: "equals",
                values: selectedAndComparatorIDs
            }]
        };

        const propertyCostResultSet = await dispatch(cubeLoadExtended(propertyCostQuery));

        const propertyCost = propertyCostResultSet.loadResponses[0].data.map(item => ({
            store: item["D_Store.StoreNaturalID"],
            propertyCost: item["F_Cost.SumPropertyCost"]
        }));

        const overallData = sales.map(current => {
            const cost = propertyCost.find(item => item.store === current.store);
            if (!cost) {
                return {
                    store: current.store,
                    sales: current.sales,
                    salesPerPoundOfPropertyCost: 0
                };
            }
            return {
                store: current.store,
                sales: current.sales,
                salesPerPoundOfPropertyCost: (cost.propertyCost === 0) ? 0 : current.sales / cost.propertyCost
            };
        });

        const selectedStore = overallData.find(item => item.store === selectedStoreID) || {};
        const comparatorStores = overallData.filter(item => comparatorStoresIDs.includes(item.store));

        dispatch(actions.getSalesPerPoundOfPropertyCostSuccess(overallData, selectedStore, comparatorStores));
    }
    catch (error) {
        dispatch(actions.getSalesPerPoundOfPropertyCostFailure());
        dispatch(logError("Error loading SalesPerPoundOfPropertyCost.", error));
    }
};

const operations = {
    getSalesPerSquareFoot,
    getPropertyCostsPerSquareFoot,
    getSalesPerPoundOfPropertyCost
};

export default operations;
